/*
 * Gráficos em SVG, escritos à mão.
 *
 * Uma biblioteca de gráficos (recharts e afins) custaria mais que o painel
 * inteiro em JavaScript, para desenhar duas formas. Aqui são barras e uma
 * linha — geometria simples, sem dependência, sem risco de a biblioteca sair
 * de manutenção.
 *
 * Acessibilidade: todo gráfico traz `role="img"` com um resumo em texto, e a
 * mesma informação aparece como tabela ao lado ou abaixo. Gráfico sozinho não
 * é informação para quem usa leitor de tela.
 */
import { Fragment, useState } from 'react';

/** Rótulo curto de data: "27/07" a partir de "2026-07-27". */
function diaCurto(iso: string): string {
  const [, mes, dia] = iso.split('-');
  return `${dia}/${mes}`;
}

export interface PontoDoDia {
  dia: string;
  total: number;
  liberados: number;
}

/**
 * Acessos por dia, em barras.
 *
 * Barra, e não linha: os dias são valores discretos e a série tem buracos
 * legítimos (dia sem acesso). Uma linha ligaria os pontos sugerindo transição
 * contínua onde não há.
 */
export function GraficoPorDia({ pontos }: { pontos: PontoDoDia[] }) {
  const maximo = Math.max(1, ...pontos.map((ponto) => ponto.total));
  const totalGeral = pontos.reduce((soma, ponto) => soma + ponto.total, 0);

  // Um rótulo a cada N barras: com 30 dias, todos se sobrepõem.
  const passo = Math.ceil(pontos.length / 10);

  return (
    <div>
      <div
        role="img"
        aria-label={`Acessos por dia: ${totalGeral} no período, máximo de ${maximo} em um dia.`}
        className="flex h-44 items-end gap-[3px]"
      >
        {pontos.map((ponto) => (
          <div
            key={ponto.dia}
            // h-full: sem altura propria, os percentuais das barras viram zero.
            className="group relative flex h-full flex-1 flex-col justify-end"
            style={{ minWidth: 4 }}
          >
            {/* Recusados ficam acima, em tom apagado: a diferença entre a barra
                cheia e a colorida é o que não foi liberado. */}
            <div
              className="w-full rounded-t-sm bg-slate-200"
              style={{ height: `${((ponto.total - ponto.liberados) / maximo) * 100}%` }}
            />
            <div
              className="w-full rounded-t-sm bg-brand-500 transition-colors group-hover:bg-brand-600"
              style={{ height: `${(ponto.liberados / maximo) * 100}%` }}
            />

            <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-2 py-1 text-xs whitespace-nowrap text-white group-hover:block">
              {diaCurto(ponto.dia)}: {ponto.liberados} liberados
              {ponto.total !== ponto.liberados ? ` · ${ponto.total - ponto.liberados} não` : ''}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-1.5 flex gap-[3px]">
        {pontos.map((ponto, indice) => (
          <span
            key={ponto.dia}
            className="flex-1 text-center text-[10px] text-slate-400"
            style={{ minWidth: 4 }}
          >
            {indice % passo === 0 ? diaCurto(ponto.dia) : ''}
          </span>
        ))}
      </div>

      <div className="mt-3 flex gap-4 text-xs text-slate-600">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-brand-500" /> Liberados
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-slate-200" /> Não liberados
        </span>
      </div>
    </div>
  );
}

export interface Fatia {
  rotulo: string;
  total: number;
}

/** Distribuição em barras horizontais, com o valor sempre legível ao lado. */
export function GraficoDeFatias({
  fatias,
  vazio = 'Sem dados no período.',
}: {
  fatias: Fatia[];
  vazio?: string;
}) {
  if (fatias.length === 0) {
    return <p className="text-sm text-slate-500">{vazio}</p>;
  }

  const total = fatias.reduce((soma, fatia) => soma + fatia.total, 0);
  const maximo = Math.max(1, ...fatias.map((fatia) => fatia.total));

  return (
    <ul className="space-y-2.5">
      {fatias.map((fatia) => (
        <li key={fatia.rotulo}>
          <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate text-slate-700">{fatia.rotulo}</span>
            <span className="shrink-0 tabular-nums text-slate-500">
              {fatia.total}
              <span className="ml-1.5 text-xs">
                ({total ? Math.round((fatia.total / total) * 100) : 0}%)
              </span>
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-brand-500"
              style={{ width: `${(fatia.total / maximo) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export interface PontoDaSerie {
  dia: string;
  valor: number;
}

/** Uma série por dia, em barras, com o valor formatado no destaque. */
export function GraficoDeSerie({
  pontos,
  rotulo,
  formatar,
}: {
  pontos: PontoDaSerie[];
  rotulo: string;
  formatar: (valor: number) => string;
}) {
  const maximo = Math.max(1, ...pontos.map((ponto) => ponto.valor));
  const passo = Math.ceil(pontos.length / 10);
  const maior = pontos.reduce<PontoDaSerie | null>(
    (atual, ponto) => (!atual || ponto.valor > atual.valor ? ponto : atual),
    null,
  );

  return (
    <div>
      <div
        role="img"
        aria-label={`${rotulo}: máximo de ${formatar(maior?.valor ?? 0)}${maior ? ` em ${diaCurto(maior.dia)}` : ''}.`}
        className="flex h-44 items-end gap-[3px]"
      >
        {pontos.map((ponto) => (
          <div
            key={ponto.dia}
            className="group relative flex h-full flex-1 flex-col justify-end"
            style={{ minWidth: 4 }}
          >
            <div
              className="w-full rounded-t-sm bg-brand-500 transition-colors group-hover:bg-brand-600"
              style={{ height: `${(ponto.valor / maximo) * 100}%` }}
            />
            <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-2 py-1 text-xs whitespace-nowrap text-white group-hover:block">
              {diaCurto(ponto.dia)}: {formatar(ponto.valor)}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-1.5 flex gap-[3px]">
        {pontos.map((ponto, indice) => (
          <span
            key={ponto.dia}
            className="flex-1 text-center text-[10px] text-slate-400"
            style={{ minWidth: 4 }}
          >
            {indice % passo === 0 ? diaCurto(ponto.dia) : ''}
          </span>
        ))}
      </div>
    </div>
  );
}

export interface CelulaDeHorario {
  /** 1 = segunda ... 7 = domingo. */
  diaDaSemana: number;
  hora: number;
  acessos: number;
  mediaSimultaneos: number;
}

const DIAS_DA_SEMANA = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const DIAS_POR_EXTENSO = [
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
  'domingo',
];

/**
 * Mapa de calor dia da semana × hora.
 *
 * Responde "quando a praça enche": a cor mais forte é o horário de maior uso
 * no período. Duas medidas, porque contam coisas diferentes — acessos é quem
 * CHEGA naquela hora; média de conectados é quem ESTÁ lá.
 */
export function MapaDeHorarios({ celulas }: { celulas: CelulaDeHorario[] }) {
  const [medida, setMedida] = useState<'acessos' | 'mediaSimultaneos'>('acessos');

  const temSimultaneos = celulas.some((celula) => celula.mediaSimultaneos > 0);
  const valorDe = (celula: CelulaDeHorario | undefined) => (celula ? celula[medida] : 0);
  const porChave = new Map(celulas.map((celula) => [`${celula.diaDaSemana}|${celula.hora}`, celula]));
  const maximo = Math.max(0, ...celulas.map(valorDe));

  if (maximo === 0 && !temSimultaneos && celulas.every((celula) => celula.acessos === 0)) {
    return <p className="text-sm text-slate-500">Sem acessos no período.</p>;
  }

  const pico = celulas.reduce<CelulaDeHorario | null>(
    (atual, celula) => (!atual || valorDe(celula) > valorDe(atual) ? celula : atual),
    null,
  );

  // Perfil por hora (todos os dias juntos): a leitura rápida de "a que horas".
  const porHora = Array.from({ length: 24 }, (_, hora) =>
    celulas
      .filter((celula) => celula.hora === hora)
      .reduce((soma, celula) => soma + valorDe(celula), 0),
  );
  const maximoPorHora = Math.max(1, ...porHora);

  const formatar = (valor: number) => valor.toLocaleString('pt-BR');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {pico && valorDe(pico) > 0 ? (
            <>
              Maior uso:{' '}
              <strong>
                {DIAS_POR_EXTENSO[pico.diaDaSemana - 1]}, das {pico.hora}h às {pico.hora + 1}h
              </strong>{' '}
              ({formatar(valorDe(pico))}{' '}
              {medida === 'acessos' ? 'acessos no período' : 'conectados em média'}).
            </>
          ) : (
            'Sem dados para esta medida no período.'
          )}
        </p>
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs shadow-sm print:hidden">
          <button
            type="button"
            onClick={() => setMedida('acessos')}
            className={`rounded-md px-2.5 py-1 font-medium ${
              medida === 'acessos' ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            Acessos
          </button>
          <button
            type="button"
            onClick={() => setMedida('mediaSimultaneos')}
            disabled={!temSimultaneos}
            title={temSimultaneos ? undefined : 'Ainda não há amostras de conectados no período'}
            className={`rounded-md px-2.5 py-1 font-medium disabled:opacity-40 ${
              medida === 'mediaSimultaneos'
                ? 'bg-brand-600 text-white'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            Média de conectados
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div
          role="img"
          aria-label={`Uso por dia da semana e hora. Maior valor: ${
            pico ? `${DIAS_POR_EXTENSO[pico.diaDaSemana - 1]} às ${pico.hora}h` : 'nenhum'
          }.`}
          className="grid min-w-[640px] gap-[2px]"
          style={{ gridTemplateColumns: '2.5rem repeat(24, minmax(0, 1fr))' }}
        >
          <span />
          {Array.from({ length: 24 }, (_, hora) => (
            <span key={hora} className="text-center text-[10px] text-slate-400">
              {hora % 3 === 0 ? `${hora}h` : ''}
            </span>
          ))}

          {DIAS_DA_SEMANA.map((rotulo, indice) => (
            <Fragment key={rotulo}>
              <span className="pr-1 text-right text-xs leading-6 text-slate-500">{rotulo}</span>
              {Array.from({ length: 24 }, (_, hora) => {
                const celula = porChave.get(`${indice + 1}|${hora}`);
                const valor = valorDe(celula);
                const intensidade = maximo ? valor / maximo : 0;
                return (
                  <div
                    key={hora}
                    className="group relative h-6 rounded-[3px]"
                    style={{
                      backgroundColor:
                        valor === 0
                          ? 'var(--color-slate-100)'
                          : `color-mix(in oklch, var(--color-brand-600) ${Math.round(15 + intensidade * 85)}%, white)`,
                    }}
                  >
                    <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-2 py-1 text-xs whitespace-nowrap text-white group-hover:block">
                      {rotulo} {hora}h: {formatar(valor)}
                    </div>
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1.5 text-xs font-medium text-slate-500">Todos os dias juntos, por hora</p>
        <div className="flex h-20 items-end gap-[3px]">
          {porHora.map((valor, hora) => (
            <div key={hora} className="group relative flex h-full flex-1 flex-col justify-end">
              <div
                className="w-full rounded-t-sm bg-brand-400 group-hover:bg-brand-600"
                style={{ height: `${(valor / maximoPorHora) * 100}%` }}
              />
              <div className="pointer-events-none absolute -top-1 left-1/2 z-10 hidden -translate-x-1/2 -translate-y-full rounded-lg bg-slate-900 px-2 py-1 text-xs whitespace-nowrap text-white group-hover:block">
                {hora}h: {formatar(Math.round(valor * 10) / 10)}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-1 flex gap-[3px]">
          {porHora.map((_, hora) => (
            <span key={hora} className="flex-1 text-center text-[10px] text-slate-400">
              {hora % 3 === 0 ? `${hora}h` : ''}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
